import React from "react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { useUnits, creatinineDisplay, creatinineUnitLabel, CREAT_US_TO_SI } from "../context/UnitsContext";

const SAGE = "#1b146d";
const TERRACOTTA = "#3b82f6";

function Panel({ title, unit, children }) {
  return (
    <div className="card-soft p-5">
      <div className="flex items-baseline justify-between">
        <h4 className="font-display text-base">{title}</h4>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
      </div>
      <div className="h-48 mt-3">{children}</div>
    </div>
  );
}

export default function LabCharts({ labs }) {
  const { units } = useUnits();
  const sorted = [...labs].sort((a, b) => a.date.localeCompare(b.date));

  const creatData = sorted
    .filter((l) => l.creatinine_mg_dl != null)
    .map((l) => ({
      date: l.date,
      value: units === "SI" ? +(l.creatinine_mg_dl * CREAT_US_TO_SI).toFixed(1) : +l.creatinine_mg_dl.toFixed(2),
    }));

  const sdmaData = sorted.filter((l) => l.sdma_ug_dl != null).map((l) => ({ date: l.date, value: l.sdma_ug_dl }));
  const upcData = sorted.filter((l) => l.upc != null).map((l) => ({ date: l.date, value: l.upc }));
  const bpData = sorted.filter((l) => l.bp_systolic != null).map((l) => ({ date: l.date, value: l.bp_systolic }));

  const commonLine = { type: "monotone", strokeWidth: 2.5, dot: { r: 4 }, activeDot: { r: 6 }, animationDuration: 800 };

  // Reference lines for creatinine stage cutoffs
  const creatRefs = units === "SI" ? [140, 250, 440] : [1.6, 2.8, 5.0];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lab-charts">
      <Panel title="Creatinine" unit={creatinineUnitLabel(units)}>
        {creatData.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={creatData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C6A61" }} />
              <YAxis tick={{ fontSize: 11, fill: "#5C6A61" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E1D8" }} />
              {creatRefs.map((r) => (
                <ReferenceLine key={r} y={r} stroke="#E5E1D8" strokeDasharray="4 4" />
              ))}
              <Line dataKey="value" stroke={SAGE} {...commonLine} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart label="No creatinine values yet" />
        )}
      </Panel>

      <Panel title="SDMA" unit="µg/dL">
        {sdmaData.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sdmaData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C6A61" }} />
              <YAxis tick={{ fontSize: 11, fill: "#5C6A61" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E1D8" }} />
              {[14, 18, 25, 45].map((r) => (
                <ReferenceLine key={r} y={r} stroke="#E5E1D8" strokeDasharray="4 4" />
              ))}
              <Line dataKey="value" stroke={TERRACOTTA} {...commonLine} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart label="No SDMA values yet" />
        )}
      </Panel>

      <Panel title="UPC" unit="ratio">
        {upcData.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={upcData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C6A61" }} />
              <YAxis tick={{ fontSize: 11, fill: "#5C6A61" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E1D8" }} />
              <ReferenceLine y={0.2} stroke="#E5E1D8" strokeDasharray="4 4" />
              <ReferenceLine y={0.4} stroke="#E5E1D8" strokeDasharray="4 4" />
              <Line dataKey="value" stroke={SAGE} {...commonLine} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart label="No UPC values yet" />
        )}
      </Panel>

      <Panel title="Systolic BP" unit="mmHg">
        {bpData.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={bpData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C6A61" }} />
              <YAxis tick={{ fontSize: 11, fill: "#5C6A61" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E1D8" }} />
              {[140, 160, 180].map((r) => (
                <ReferenceLine key={r} y={r} stroke="#E5E1D8" strokeDasharray="4 4" />
              ))}
              <Line dataKey="value" stroke={TERRACOTTA} {...commonLine} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyChart label="No BP values yet" />
        )}
      </Panel>
    </div>
  );
}

function EmptyChart({ label }) {
  return <div className="h-full flex items-center justify-center text-xs text-muted-foreground">{label}</div>;
}

export { creatinineDisplay };
